// Gain between the microphone and the call.
//
// A quiet input is the other half of the silence problem: the device is picking something up, but
// far too little for the wearer to make out over a worksite. The operator cannot fix that in macOS
// mid-emergency, so the call carries its own gain stage.
//
// The track the call publishes becomes the OUTPUT of this graph, so the level meter, the silence
// detector and the wearer all measure the same signal — what is actually being transmitted, not what
// the microphone happened to capture.
//
// SAFETY RULE, and the reason for every fallback below: an AudioContext that will not run must never
// cost the operator their voice. A suspended context produces a destination track of pure silence,
// which would turn a working call into the exact fault this feature exists to cure. Whenever the
// graph cannot be built or started, the RAW track is used unchanged and the caller is told gain is
// unavailable.

export interface MicChain {
  /** What to publish. The processed stream, or the raw one when gain could not be applied. */
  stream: MediaStream
  /** False when the browser refused us an audio graph — the raw track is being used. */
  gainAvailable: boolean
  setGain: (value: number) => void
  close: () => void
}

/** Full range offered to the operator. 1 is the microphone untouched. */
export const MIN_GAIN = 0
export const MAX_GAIN = 4
export const DEFAULT_GAIN = 1

export function clampGain(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_GAIN

  return Math.min(MAX_GAIN, Math.max(MIN_GAIN, value))
}

function audioContextCtor(): typeof AudioContext | undefined {
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
}

/**
 * Build the gain stage around a captured microphone.
 *
 * Returns the raw stream untouched when there is nothing to do (gain of exactly 1, no AudioContext,
 * or a context that will not start) — so the ordinary call is byte-for-byte the path that has been
 * working, and the graph is only ever inserted when the operator has asked for it.
 */
export function createMicChain(raw: MediaStream, gain: number): MicChain {
  const wanted = clampGain(gain)
  const Ctor = audioContextCtor()
  const bail = (): MicChain => ({ stream: raw, gainAvailable: false, setGain: () => undefined, close: () => undefined })

  if (!Ctor) return bail()

  let ctx: AudioContext

  try {
    ctx = new Ctor()
  } catch {
    return bail()
  }

  // Chrome starts a context suspended until the page has been interacted with. `resume` is
  // fire-and-forget: the call must not wait on it, and if it never runs the watchdog below swaps
  // the raw track back in.
  void ctx.resume?.().catch(() => undefined)

  try {
    const source = ctx.createMediaStreamSource(raw)
    const gainNode = ctx.createGain()
    const dest = ctx.createMediaStreamDestination()

    gainNode.gain.value = wanted
    source.connect(gainNode)
    gainNode.connect(dest)

    return {
      stream: dest.stream,
      gainAvailable: true,
      setGain: value => {
        gainNode.gain.value = clampGain(value)
      },
      close: () => {
        try {
          source.disconnect()
          gainNode.disconnect()
        } catch {
          // Already torn down with the context.
        }

        void ctx.close?.().catch(() => undefined)
        // The RAW tracks are the caller's to stop — this graph does not own the microphone.
      },
    }
  } catch {
    void ctx.close?.().catch(() => undefined)

    return bail()
  }
}
